import { useRef, useEffect, useState, useCallback, ChangeEvent } from 'react'
import { PackageModel, PaginateModel } from '../membership/models'
import { startSxpProxy } from '../../utils/api'
import '../membership/membership.scss'
import {
  ADMIN_PROJECT_ID,
  PACKAGE_PROJECT_ID,
  pageSizeOptions,
} from '../../utils/constants'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { selectSelectedLocationId } from '../location/locationSlice'
import { useNavigate } from 'react-router-dom'
import MaskedNumber from '../../components/MaskedNumber'
import {
  selectQuery,
  selectParam,
  setQueryParam,
} from '../membership/membershipSlice'
import PackageSearch from './PackageSearch'
import SelectableSearch from '../../components/SelectableSearch'
import { packageName } from '../Radiology/Constants/constants'
import ProvenanceView from '../../components/ProvenanceView'
import { odooPharmacyLink } from '../appointments/models'
import { ColDef } from 'ag-grid-community'
import { Box, Button, useTheme } from '@mui/material'
import MedAgDataGrid from '../../components/MedAgDataGrid'
import { notPaid, packageData, selectbalePackageOptions } from './constants'
import './package.scss'
import useCustomStyles from '../../utils/hooks/CustomStylesHook'
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { paidTitleCaseString, unpaidString } from '../AB Membership/constants'
import DropdownMenu from '../../components/DropdownMenu/DropdownMenu'
import { exportExcel } from '../../utils/hooks/Utils'
import { toast } from 'react-toastify'
import { dropDownMenuKeys, noDataMessage } from '../../components/constants'

const styles = () => ({
  centerHeader: {
    color: '#ffffff',
    textAlign: 'center',
    '& .ag-header-cell-text': {
      flex: 1,
    },
  },
  MedAgDataGridExportIcon: {
    background: '#4682b4',
    color: '#ffffff',
    borderRadius: 5,
  },
  disabledBtn: {
    background: 'grey',
    cursor: 'none',
  },
  unpaidLink: {
    textTransform: 'initial',
    outline: 'border-box',
    background: '#E35335',
    color: 'white',
    padding: '0px 2px',
    fontSize: '10px',
    '&:hover': {
      background: '#E35335',
    },
  },
  paidLink: {
    textTransform: 'initial',
    outline: 'border-box',
    background: 'green',
    color: 'white',
    padding: '0px 2px',
    fontSize: '10px',
    '&:hover': {
      background: 'green',
    },
  },
  endIcon: {
    fontSize: '13px !important',
  },
})
const Packages = () => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const query = useAppSelector(selectQuery)
  const param = useAppSelector(selectParam)
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const [currentPage, setCurrentPage] = useState(1)
  const [limit, setLimit] = useState(100)
  const [paginate, setPaginate] = useState<PaginateModel>({
    totalCount: 0,
    totalPages: 0,
    data: [],
  })
  const [packages, setPackages] = useState<PackageModel[]>([])
  const location = useAppSelector(selectSelectedLocationId)

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
  }

  useEffect(() => {
    setCurrentPage(1)
  }, [limit, query, param])

  const getPackages = useCallback(() => {
    const state = {
      location,
      limit: limit,
      offset: (currentPage - 1) * limit,
    }
    startSxpProxy(PACKAGE_PROJECT_ID, 'purchasedPackages', state)
      .then((data) => {
        const members: PackageModel[] = data?.data?.data ?? []
        setPackages(members)
        setPaginate({
          totalCount: data?.data?.total_count,
          totalPages: data?.data?.total_pages,
          data: data?.data?.data,
        })
      })
      .catch((err) => {
        console.log(err)
        setPackages([])
        setPaginate({
          totalCount: 0,
          totalPages: 0,
          data: [],
        })
      })
      .finally(() => setLoading(false))
  }, [location, limit, currentPage])

  const membershipTableContainerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const resizeHandler = () => {
      const container = membershipTableContainerRef.current
      if (container) {
        const availableHeight =
          window.innerHeight - container.getBoundingClientRect().top
        container.style.maxHeight = `${availableHeight - 60}px`
      }
    }
    window.addEventListener('resize', resizeHandler)
    resizeHandler()
    return () => {
      window.removeEventListener('resize', resizeHandler)
      dispatch(setQueryParam({ q: '', param: '' }))
    }
  }, [dispatch])

  const handleCreateMembership = () => {
    navigate('/package/create')
  }

  const openOdooInvoice = (id: any) => {
    startSxpProxy(ADMIN_PROJECT_ID, odooPharmacyLink, {
      invoiceId: id,
      location: location,
    })
      .then((data) => {
        const url = data?.data?.auth_link
        window.open(url, '_blank', 'noopener,noreferrer')
      })
      .catch((err) => console.error(err, 'err'))
  }

  const handleSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setLimit(Number(e.target.value))
  }

  const handleSearch = useCallback(
    (query: string, param: string) => {
      let q = ''
      switch (query) {
        case 'ID':
          q = 'partner'
          break
        case 'Phone':
          q = 'phone'
          break
        case 'Name':
          q = 'name'
          break
        default:
          break
      }

      dispatch(setQueryParam({ q, param }))
      if (param.trim() === '') {
        getPackages()
      } else {
        startSxpProxy(PACKAGE_PROJECT_ID, 'getPackageForPatient', {
          location: location,
          muhid: param,
        })
          .then((data: any) => {
            if (data?.first_pack_payment === true) {
              setPaginate({
                totalCount: 0,
                totalPages: 0,
                data: [],
              })
              setCurrentPage(0)
            } else {
              const members: PackageModel[] = data?.data?.data ?? []
              setPackages(members)
              setPaginate({
                totalCount: data?.data?.total_count,
                totalPages: data?.data?.total_pages,
                data: data?.data?.data,
              })
              setCurrentPage(1)
            }
          })
          .catch((error: any) => {
            console.error('API Error:', error)
          })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, getPackages]
  )
  const handleExport = () => {
    if (packages?.length > 0) {
      exportExcel(packageData(packages))
    } else {
      toast.error(noDataMessage)
    }
  }
  const handleSelect = (e: any) => {
    if (e?.[0] === dropDownMenuKeys?.export) {
      handleExport()
    }
  }

  useEffect(() => {
    setLoading(true)
    getPackages()
  }, [getPackages])

  const index = (currentPage - 1) * limit
  let startIndex = index + 1
  let endIndex = index + limit

  if (
    !isNaN(paginate.totalCount) &&
    paginate.totalCount !== null &&
    paginate.totalCount !== undefined &&
    paginate.totalCount > 0
  ) {
    startIndex = Math.min(startIndex, paginate.totalCount)
    endIndex = Math.min(endIndex, paginate.totalCount)
  } else {
    startIndex = 0
    endIndex = 0
  }

  const handleSearchRef = useRef(handleSearch)
  useEffect(() => {
    handleSearchRef.current = handleSearch
  }, [handleSearch])

  const handleSearchWithRef = useCallback((query: string, param: string) => {
    handleSearchRef.current(query, param)
  }, [])

  const formatDate = (dateTimeString: string) => {
    const dateTime = new Date(dateTimeString)
    return `${dateTime.toDateString()}`
  }

  const formatTime = (dateTimeString: any) => {
    if (dateTimeString) {
      const dateTime = new Date(dateTimeString)
      const utcTimestamp = dateTime.getTime()
      const istTimestamp = utcTimestamp + 5.5 * 60 * 60 * 1000
      const istDate = new Date(istTimestamp)
      const hours = istDate.getHours().toString().padStart(2, '0')
      const minutes = istDate.getMinutes().toString().padStart(2, '0')
      const seconds = istDate.getSeconds().toString().padStart(2, '0')
      return `${hours}:${minutes}:${seconds}`
    } else {
      return ''
    }
  }
  const handlePaymentLink = (params: any) => {
    const d = params?.data
    return (
      <Button
        size='small'
        onClick={() => openOdooInvoice(d?.id)}
        className={`${
          d?.payment_state === notPaid ? classes?.unpaidLink : classes?.paidLink
        }`}
        endIcon={
          d?.payment_state === notPaid ? (
            <OpenInNewIcon className={classes?.endIcon} />
          ) : (
            <CheckCircleOutlineIcon className={classes?.endIcon} />
          )
        }
      >
        {d?.payment_state === notPaid ? unpaidString : paidTitleCaseString}
      </Button>
    )
  }

  const columnDefs: ColDef[] = [
    {
      headerName: 'SL No',
      field: 'slNo',
      width: 80,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) =>
        (currentPage - 1) * limit + (params?.node?.rowIndex + 1),
      headerClass: 'custom-header-cell',
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'UHID',
      field: 'uhid',
      minWidth: 180,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => params?.data?.partner_id?.ref,
      headerClass: 'custom-header-cell',
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'left' },
    },
    {
      headerName: 'Name',
      field: 'name',
      minWidth: 340,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => params?.data?.partner_id?.name ?? '-',
      headerClass: 'custom-header-cell',
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'left',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        display: 'flex',
        lineHeight: 1.5,
      },
      wrapText: true,
    },
    {
      headerName: 'Mobile No.',
      field: 'mobile',
      minWidth: 180,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        const d = params?.data
        return d?.partner_id?.phone ? (
          <MaskedNumber mobile={d?.partner_id?.phone} />
        ) : (
          '-'
        )
      },
      headerClass: 'custom-header-cell',
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'left' },
    },
    {
      headerName: 'Operating Unit',
      field: 'slNo',
      minWidth: 140,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) =>
        params?.data?.operating_unit_id?.[1]?.split(' ')?.[1] ?? 'Loc',
      headerClass: 'custom-header-cell',
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Package',
      field: 'package',
      minWidth: 390,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) =>
        params?.data?.invoice_line_ids[0]?.name ?? '-',
      headerClass: 'custom-header-cell',
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'left',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        display: 'flex',
        lineHeight: 1.5,
      },
      wrapText: true,
    },
    {
      headerName: 'InvoiceId',
      field: 'invoiceId',
      minWidth: 255,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => params?.data?.name ?? '-',
      headerClass: classes?.centerHeader,
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Pay.Status',
      field: 'slNo',
      minWidth: 120,
      flex: 1,
      sortable: false,
      filter: false,
      cellRenderer: handlePaymentLink,
      headerClass: 'custom-header-cell',
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Created On',
      field: 'createdOn',
      minWidth: 200,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => (
        <ProvenanceView
          name={params?.data?.thop_user_name || '-'}
          date={
            formatDate(params?.data?.invoice_date_time) +
            ' ' +
            formatTime(params?.data?.invoice_date_time)
          }
          mode={'CREATE'}
        />
      ),
      headerClass: 'custom-header-cell',
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'left',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        display: 'flex',

        lineHeight: 1.5,
      },
      wrapText: true,
    },
  ]

  return (
    <div className='layoutcontent total-width-content'>
      <div className='package-main-container'>
        <div className='membership-dashboard-text'>{packageName}</div>
      </div>
      <Box height={'96%'} width={'100%'} marginTop={'-28px'}>
        <MedAgDataGrid
          tableHeight={'100%'}
          rowData={packages}
          columnDefs={columnDefs}
          pagination={false}
          searchField={false}
          exportIcon={false}
          headerHeight={30}
          localSearch={false}
          filterActions={
            <>
              <DropdownMenu
                onSelect={handleSelect}
                filterOptions={[
                  {
                    label: dropDownMenuKeys?.activeKey,
                    value: dropDownMenuKeys?.active,
                  },
                  {
                    label: dropDownMenuKeys?.inActiveKey,
                    value: dropDownMenuKeys?.inActive,
                  },
                ]}
                actionOptions={[
                  {
                    label: dropDownMenuKeys?.exportKey,
                    value: dropDownMenuKeys?.export,
                  },
                ]}
                selectedId={false}
              />
              <SelectableSearch
                items={selectbalePackageOptions}
                onSearch={handleSearchWithRef}
              />
              <PackageSearch onCreate={handleCreateMembership} />
            </>
          }
          enableCellTextSelection={true}
          loading={loading ? 'Loading' : false}
        />
      </Box>
      <div className='text-right pagination-main-content'>
        <div className='inline-block membership-pagination text-right'>
          <select
            className='rowsperpage pagination-container'
            defaultValue={limit}
            onChange={handleSizeChange}
          >
            {pageSizeOptions?.map((size: number) => (
              <option key={size}>{size}</option>
            ))}
          </select>
          <span>
            <span className='mr10 pagination-container'>
              {startIndex}-{endIndex}/{paginate?.totalCount}{' '}
            </span>
          </span>
        </div>

        <div className='pagination-count-content'>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            className={`icon-disable-btn ${
              currentPage !== 1 ? '' : 'disableBtn'
            }`}
            disabled={currentPage === 1}
          >
            <KeyboardArrowLeft />
          </button>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            className={`icon-disable-btn ${
              currentPage !== paginate?.totalPages ? '' : 'disableBtn'
            }`}
            disabled={currentPage === paginate?.totalPages}
          >
            <KeyboardArrowRight />
          </button>
        </div>
      </div>
    </div>
  )
}
export default Packages
